/**
 * Customer Portal (UI) - support ticket listing route.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchOpenSupportTickets } from '../features/api/client';
import type { OpenSupportTicket } from '../types/portal';
import type { TimeDisplayMode } from '../utils/datetime';
import { formatPortalDateTime } from '../utils/datetime';

type SupportTicketsListRouteProps = {
  accountId: string | null;
  timeDisplayMode: TimeDisplayMode;
};

export function SupportTicketsListRoute({ accountId, timeDisplayMode }: SupportTicketsListRouteProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<OpenSupportTicket[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      setError(null);
      setTickets([]);
      setMessage('Select an account to view open support tickets.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOpenSupportTickets(accountId)
      .then((payload) => {
        if (cancelled) return;
        setTickets(payload.tickets ?? []);
        setMessage(payload.message ?? null);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h2 className="page-title mb-0">Support</h2>
        <Link to="/support/new" className="btn btn-primary">
          Create new ticket
        </Link>
      </div>

      {loading ? <div className="alert alert-info">Loading open support tickets...</div> : null}
      {!loading && error ? <div className="alert alert-danger mb-3">{error}</div> : null}
      {!loading && !error && message ? <div className="alert alert-secondary mb-3">{message}</div> : null}

      {!loading && !error ? (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title mb-0">Open tickets</h3>
          </div>
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="w-1">View</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{ticket.ticketNumber ?? ticket.id}</td>
                    <td>{ticket.subject}</td>
                    <td>{ticket.status}</td>
                    <td>
                      {ticket.createdTime
                        ? formatPortalDateTime(ticket.createdTime, timeDisplayMode, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })
                        : '-'}
                    </td>
                    <td>
                      {ticket.webUrl ? (
                        <a href={ticket.webUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                          Open
                        </a>
                      ) : (
                        <span className="text-secondary">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!tickets.length ? (
                  <tr>
                    <td colSpan={5} className="text-secondary">
                      No open tickets found for your email address.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}
